import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "./brand/Logo";

const NAV = [
  { to: "/#approach", label: "Approach" },
  { to: "/#systems", label: "What we design" },
  { to: "/#proof", label: "Projects" },
  { to: "/what-could-we-build", label: "What could we build?" },
  { to: "/#franchise", label: "Franchise" },
  {
    to: "/hospitality-consulting",
    label: "Hospitality",
    mobileLabel: "Hospitality Consulting",
  },
  { to: "/#sectors", label: "Sectors" },
  { to: "/insights", label: "Insights" },
  { to: "/#enquiry", label: "Contact" },
];

const CTA_LABEL = "Start a conversation";
const CTA_TO = "/#enquiry";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div
          className={`flex items-center justify-between rounded-2xl transition-all duration-500 ${
            scrolled ? "glass-strong px-4 sm:px-5 py-2.5" : "px-2 sm:px-3 py-2"
          }`}
        >
          <Logo variant="mark" height={44} />

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item) => {
              const isHashLink = item.to.includes("#");
              if (isHashLink) {
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={`nav-link-${item.label.toLowerCase()}`}
                    className="px-3 py-2 text-[12.5px] font-medium tracking-[0.01em] whitespace-nowrap rounded-full text-mute-200 hover:text-mute-100 transition-colors"
                  >
                    {item.label}
                  </Link>
                );
              }
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-link-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `px-3 py-2 text-[12.5px] font-medium tracking-[0.01em] whitespace-nowrap rounded-full transition-colors ${
                      isActive ? "text-mute-100" : "text-mute-200 hover:text-mute-100"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to={CTA_TO}
              data-testid="nav-cta-book-call"
              className="hidden md:inline-flex btn-primary text-[13px] py-2.5 px-5"
            >
              {CTA_LABEL}
              <ArrowUpRight className="w-4 h-4" />
            </Link>
            <button
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="mobile-menu-toggle"
              onClick={(e) => {
                e.stopPropagation();
                setOpen((o) => !o);
              }}
              className="relative z-[60] lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full glass"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden fixed inset-0 top-0 z-40"
          style={{ background: "rgba(7,7,7,0.94)", backdropFilter: "blur(22px)" }}
        >
          <div className="pt-24 px-6 pb-8 flex flex-col h-full">
            <div className="mb-6 flex justify-center">
              <Logo variant="full" height={68} testId="mobile-menu-logo" as="div" />
            </div>
            <nav className="flex flex-col gap-1">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className="font-display text-3xl py-3 border-b border-white/5 text-mute-100 hover:text-gold transition-colors"
                >
                  {item.mobileLabel || item.label}
                </Link>
              ))}
            </nav>
            <div className="mt-auto pt-6">
              <Link
                to={CTA_TO}
                data-testid="mobile-cta-book-call"
                onClick={() => setOpen(false)}
                className="btn-primary w-full justify-center"
              >
                {CTA_LABEL}
                <ArrowUpRight className="w-4 h-4" />
              </Link>
              <p className="mt-4 text-xs text-mute-300 text-center">
                A considered conversation about your digital operations.
              </p>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
